import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';

import { useNewProject } from '@contexts/NewProject.context';
import { useAuth } from '@contexts/User.context';
import { PROJECT_MODE } from '@utils/constants';

// Helpers
import { titleCaseReplace } from '@utils/helpers';

// JSX
import View from '@components/layout/views/View';
import Loader from '@components/loading/Loader';
import ProjectLimitWarning from './components/ProjectLimitWarning';
import Step from './components/Step';
import SelectProjectType from './components/SelectProjectType';
import AddProjectDetails from './components/AddProjectDetails';
import UploadProjectFiles from './components/uploadProjectFiles/UploadProjectFiles';
import UploadProgressModal from './components/UploadProgressModal';
import ErrorModal from '@components/modal/ErrorModal';
import AdvancedActions from './components/uploadProjectFiles/components/AdvancedActions';
import FormActions from './components/uploadProjectFiles/components/FormActions';
import VideoProjectWizard from './components/video/VideoProjectWizard';

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	column-gap: 20px;
	padding-bottom: 2.5rem;
`;

const NewProject = () => {
	const { monthlyProjectsLimit, mode, submitError, clearSubmitError } = useNewProject();
	const { watch } = useFormContext();
	const { isEnterprise, tierPro, currentUser } = useAuth();
	const { VIDEO } = PROJECT_MODE;

	const { active_org_name: activeOrgName } = currentUser ?? {};

	const projectName = watch('projectName');

	const viewTitle = isEnterprise
		? `Create new project for ${activeOrgName}`
		: 'Create new project';

	const { isLoading: monthlyProjectsLimitIsLoading, projectLimitReached } =
		monthlyProjectsLimit ?? {};

	if (monthlyProjectsLimitIsLoading && tierPro)
		return (
			<View title="Create new project">
				<Loader />
			</View>
		);

	if (!monthlyProjectsLimitIsLoading && projectLimitReached)
		return (
			<View title="Create new project">
				<ProjectLimitWarning />
			</View>
		);

	// A video project runs in its own full-page three-column flow from the moment "Video" is picked
	if (mode === VIDEO) return <VideoProjectWizard />;

	return (
		<View title={viewTitle}>
			<Step step="1" header="Choose Project Type">
				<SelectProjectType />
			</Step>

			<Step step="2" header={`Add ${titleCaseReplace(mode)} Project Details`} active={!!mode}>
				<AddProjectDetails />
			</Step>

			<Step step="3" header="Upload Project Files" active={!!projectName}>
				<UploadProjectFiles />
				<Grid>
					<AdvancedActions />
					<FormActions />
				</Grid>
			</Step>

			<UploadProgressModal />
			<ErrorModal
				show={!!submitError}
				onClose={clearSubmitError}
				title="Project creation failed"
				text="An error occurred while setting up your project. Please verify
				your input and try again. If the issue persists, contact support
				for assistance."
			/>
		</View>
	);
};

export default NewProject;
